import { HTMLAttributes, PropsWithChildren } from 'react';

import Lottie from 'lottie-react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { useTranslation } from 'react-i18next';

import { sendFEEvent } from '../../../../../../eventUtil';
import { ESdkEvents } from '../../../../../../contracts';
import { LockIcon } from '../../../../../../assets/icons';
import { useFooterText } from '../../hooks/useFooterText';
import { useCheckout } from '../../../../../../hooks/use-checkout';
import { UsaStates } from '../../../../../../constants/enums';
import newLoader from '../../../../../../assets/newLoader.json';
import { openNewTab } from '../../../../../../utils/open-new-tab';
import { useAppConfig } from '../../../../../../hooks/use-app-config';
import { usePaymentState } from '../../../../hooks/use-payment-state';
import { FormattedPrice } from '../../../FormattedPrice/FormattedPrice';
import { getIsApplovin, getIsKingPublisher, getIsVizorPublisher, getTOS } from './utils';

import './styles.scss';

interface PayButtonProps {
  onClick: () => void;
  buttonText?: string;
}

const EULA_URL = 'https://appcharge.com/end-user-license-agreement';
export const PRIVACY_POLICY_URL = 'https://appcharge.com/payments-privacy-policy/';
export const KING_TOS_URL = 'https://www.king.com/termsandconditions/';
export const VIZOR_USER_AGREEMENT_URL = 'https://docs.vizor-games.com/terms/user-agreement/en';
export const VIZOR_TOS_URL = 'https://klondikeadventures.com/documents/terms_of_service';
export const VIZOR_PRIVACY_POLICY_URL = 'https://klondikeadventures.com/documents/privacy_notice';

export function PayButton({ onClick, buttonText, children }: PropsWithChildren<PayButtonProps>) {
  const { t } = useTranslation(undefined, { keyPrefix: 'checkout' });
  const { getEventParams, checkoutData } = useCheckout();
  const { theme, publisherId, featureFlags } = useAppConfig();
  const customerGeoState = usePaymentState((state) => state.customerGeoState);
  const priceBreakdown = usePaymentState((state) => state.priceBreakdown);
  const isPaymentLoading = usePaymentState((state) => state.isPaymentLoading);
  const { footerText } = useFooterText();

  const pricing = checkoutData?.pricing;
  const priceTotal = priceBreakdown?.priceTotal;

  const isWooga = Boolean(featureFlags?.checkout_footer_text_wooga);
  const displaySecurePaymentSubtext = featureFlags?.checkout_secure_payment_subtext;
  const displayFooterTextFromBackend = featureFlags?.checkout_footer_text_from_backend;

  const state =
    customerGeoState ||
    checkoutData?.customer.address?.state ||
    checkoutData?.customer.geoLocator.state;
  const isCalifornia = state?.toLowerCase() === UsaStates.CALIFORNIA;

  const isKingPublisher = getIsKingPublisher(publisherId);
  const isVizorPublisher = getIsVizorPublisher(publisherId);
  const isApplovin = getIsApplovin(publisherId);

  const text = buttonText || t('pay');

  const handleClick = () => {
    sendFEEvent(ESdkEvents.ON_PAYMENT_INTENT, { ...getEventParams() });
    onClick();
  };

  return (
    <Box className={`button-conatiner`}>
      <button
        className={`payment-button ${isPaymentLoading ? 'load-animation' : ''}`}
        style={{ backgroundColor: theme?.button.color }}
        disabled={isPaymentLoading}
        id="submit"
        onClick={handleClick}
      >
        <span
          id="button-text"
          style={{
            color: theme?.button.textColor,
          }}
        >
          {isPaymentLoading ? (
            <Lottie animationData={newLoader} className="button-loader" />
          ) : (
            <Stack direction={'row'} justifyContent={'center'} spacing={0.5}>
              <Box>{text}</Box>
              <FormattedPrice pricing={pricing} priceValue={priceTotal} />
            </Stack>
          )}
        </span>
      </button>
      {children && <Box pt={1}>{children}</Box>}
      {displayFooterTextFromBackend && footerText && (
        <Box
          pt={1}
          className="footer-text"
          fontSize={'10px'}
          textAlign={'center'}
          color={'#323241'}
          data-testid="checkout-legal-disclaimer"
          dangerouslySetInnerHTML={{ __html: footerText }}
        />
      )}
      {!displayFooterTextFromBackend && (
        <>
          <Box
            pt={1}
            fontSize={'10px'}
            textAlign={'center'}
            color={'#323241'}
            data-testid="checkout-legal-disclaimer"
          >
            <Box component={'span'}>{t('payButtonDisclaimerPrefix')}</Box>{' '}
            <DisclaimerLinkButton onClick={() => openNewTab(EULA_URL)}>
              {t('endUserLicenseAgreement')}
            </DisclaimerLinkButton>{' '}
            <Box component={'span'}>{t('and')}</Box>{' '}
            <DisclaimerLinkButton onClick={() => openNewTab(PRIVACY_POLICY_URL)}>
              {t('privacyPolicy')}
            </DisclaimerLinkButton>
            {isKingPublisher && (
              <>
                {' '}
                <Box component={'span'}>{t('and')}</Box> <Box component={'span'}>{t('king')}</Box>{' '}
                <DisclaimerLinkButton onClick={() => openNewTab(KING_TOS_URL)}>
                  {t('termsAndConditions')}
                </DisclaimerLinkButton>
              </>
            )}
            {isVizorPublisher && (
              <>
                {' '}
                <Box component={'span'}>{t('and')}</Box> <Box component={'span'}>{t('vizor')}</Box>{' '}
                <DisclaimerLinkButton onClick={() => openNewTab(VIZOR_USER_AGREEMENT_URL)}>
                  {t('userAgreement')}
                </DisclaimerLinkButton>
                {', '}
                <DisclaimerLinkButton onClick={() => openNewTab(VIZOR_TOS_URL)}>
                  {t('termsOfService')}
                </DisclaimerLinkButton>{' '}
                <Box component={'span'}>{t('and')}</Box>{' '}
                <DisclaimerLinkButton onClick={() => openNewTab(VIZOR_PRIVACY_POLICY_URL)}>
                  {t('privacyNotice')}
                </DisclaimerLinkButton>
              </>
            )}
          </Box>
          <PublishersSpecificText
            isCalifornia={isCalifornia}
            isApplovin={isApplovin}
            isWooga={isWooga}
          />
        </>
      )}
      {displaySecurePaymentSubtext && (
        <Box display={'flex'} alignItems={'center'} justifyContent={'center'} pt={0.5}>
          <LockIcon />
          <Box fontSize={'10px'} color={'#323241'}>
            {t('safeAndCharged')}
          </Box>
        </Box>
      )}
    </Box>
  );
}

function DisclaimerLinkButton({
  children,
  ...rest
}: PropsWithChildren<HTMLAttributes<HTMLSpanElement>>) {
  return (
    <Box component={'span'} {...rest} style={{ textDecoration: 'underline', cursor: 'pointer' }}>
      {children}
    </Box>
  );
}

// this component needs to be removed after the checkout_footer_text_from_backend FF is removed
function PublishersSpecificText({
  isCalifornia,
  isApplovin,
  isWooga,
}: {
  isCalifornia: boolean;
  isApplovin: boolean;
  isWooga: boolean;
}) {
  const { t } = useTranslation(undefined, { keyPrefix: 'checkout' });
  const { publisherId } = useAppConfig();
  const applovinTOS = getTOS(publisherId);

  return (
    <>
      {isCalifornia && !isApplovin && !isWooga && (
        <Box pt={0.5} fontSize={'10px'} textAlign={'center'} color={'#323241'}>
          <Box component={'span'}>{t('legalDisclaimerForUSA')}</Box>{' '}
          <DisclaimerLinkButton onClick={() => openNewTab(EULA_URL)}>
            {t('endUserLicenseAgreement')}
          </DisclaimerLinkButton>
        </Box>
      )}
      {isCalifornia && isApplovin && (
        <Box pt={0.5} fontSize={'10px'} textAlign={'center'} color={'#323241'}>
          <Box component={'span'}>{t('legalDisclaimerForApplovinUSA')}</Box>{' '}
          <DisclaimerLinkButton onClick={() => openNewTab(applovinTOS)}>
            {t('termsAndConditions')}
          </DisclaimerLinkButton>
        </Box>
      )}
      {isCalifornia && isWooga && (
        <Box pt={0.5} fontSize={'10px'} textAlign={'center'} color={'#323241'}>
          <Box component={'span'}>{t('woogaLegalDisclaimerCA')}</Box>
        </Box>
      )}
      {!isCalifornia && isWooga && (
        <Box pt={0.5} fontSize={'10px'} textAlign={'center'} color={'#323241'}>
          <Box component={'span'}>{t('woogaLegalDisclaimer')}</Box>
        </Box>
      )}
    </>
  );
}
